import React, { useRef, useEffect } from 'react';
import { ArrowRight, Users, Zap } from 'lucide-react';
import { Link } from 'react-router-dom';
import { loginWithDiscord } from '../lib/api';

const Landing = () => {
  const videoRef = useRef(null);

  useEffect(() => {
    // Garantir que o vídeo esteja em loop e sem som
    if (videoRef.current) {
      videoRef.current.play().catch(err => {
        console.log('Auto-play prevented:', err);
      });
    }
  }, []);

  const handleDiscordLogin = async () => {
    try {
      await loginWithDiscord();
    } catch (error) {
      console.error('Erro ao fazer login com Discord:', error);
    }
  };


  return (
    <div className="relative w-full h-screen overflow-hidden text-white font-sans selection:bg-fuchsia-500 selection:text-white">
      
      {/* Video Background */}
      <video
        ref={videoRef}
        autoPlay
        loop
        muted
        playsInline
        className="absolute inset-0 w-full h-full object-cover"
      >
        <source src="/background-video.mp4" type="video/mp4" />
        Seu navegador não suporta vídeos HTML5.
      </video>

      {/* Overlay escuro para contraste */}
      <div className="absolute inset-0 bg-gradient-to-r from-black/90 via-black/50 to-purple-900/30" />
      {/* Scanlines effect */}
      <div className="absolute inset-0 bg-[linear-gradient(rgba(18,16,16,0)_50%,rgba(0,0,0,0.25)_50%),linear-gradient(90deg,rgba(255,0,0,0.06),rgba(0,255,0,0.02),rgba(0,0,255,0.06))] z-0 bg-[length:100%_4px,3px_100%] pointer-events-none" />

      {/* CSS para o botão shiny */}
      <style>{`
        @property --gradient-angle {
          syntax: "<angle>";
          initial-value: 0deg;
          inherits: false;
        }

        @property --gradient-angle-offset {
          syntax: "<angle>";
          initial-value: 0deg;
          inherits: false;
        }

        @property --gradient-percent {
          syntax: "<percentage>";
          initial-value: 5%;
          inherits: false;
        }

        @property --gradient-shine {
          syntax: "<color>";
          initial-value: white;
          inherits: false;
        }

        .shiny-button {
          --animation: gradient-angle linear infinite;
          --duration: 3s;
          --shadow-size: 2px;
          --shiny-cta-bg: #000000;
          --shiny-cta-bg-subtle: #1a1818;
          --shiny-cta-fg: #ffffff;
          --shiny-cta-highlight: #22d3ee;
          --shiny-cta-highlight-subtle: white;
          
          isolation: isolate;
          position: relative;
          overflow: hidden;
          cursor: pointer;
          outline-offset: 4px;
          padding: 1.25rem 2.5rem;
          line-height: 1.2;
          border: 1px solid transparent;
          border-radius: 360px;
          color: var(--shiny-cta-fg);
          font-size: 16px;
          font-weight: 600;
          text-decoration: none;
          display: inline-block;
          
          background: 
            linear-gradient(var(--shiny-cta-bg), var(--shiny-cta-bg)) padding-box,
            conic-gradient(
              from calc(var(--gradient-angle) - var(--gradient-angle-offset)),
              transparent,
              var(--shiny-cta-highlight) var(--gradient-percent),
              var(--gradient-shine) calc(var(--gradient-percent) * 2),
              #d946ef calc(var(--gradient-percent) * 3),
              transparent calc(var(--gradient-percent) * 4)
            ) border-box;
          
          box-shadow: inset 0 0 0 1px var(--shiny-cta-bg-subtle);
          
          transition: 800ms cubic-bezier(0.25, 1, 0.5, 1);
          transition-property: --gradient-angle-offset, --gradient-percent, --gradient-shine;
          
          animation: var(--animation) var(--duration),
                     var(--animation) calc(var(--duration) / 0.4) reverse paused;
          animation-composition: add;
        }

        .shiny-button::before,
        .shiny-button::after,
        .shiny-button .button-text::before {
          content: "";
          pointer-events: none;
          position: absolute;
          left: 50%;
          top: 50%;
          transform: translate(-50%, -50%);
          z-index: -1;
        }

        .shiny-button::before {
          --size: calc(100% - var(--shadow-size) * 3);
          --position: 2px;
          --space: calc(var(--position) * 2);
          
          width: var(--size);
          height: var(--size);
          background: radial-gradient(
            circle at var(--position) var(--position),
            white calc(var(--position) / 4),
            transparent 0
          ) padding-box;
          background-size: var(--space) var(--space);
          background-repeat: space;
          mask-image: conic-gradient(
            from calc(var(--gradient-angle) + 45deg),
            black,
            transparent 10% 90%,
            black
          );
          border-radius: inherit;
          opacity: 0.4;
          
          animation: var(--animation) var(--duration),
                     var(--animation) calc(var(--duration) / 0.4) reverse paused;
          animation-composition: add;
        }

        .shiny-button::after {
          --animation: shimmer linear infinite;
          
          width: 100%;
          aspect-ratio: 1;
          background: linear-gradient(
            -50deg,
            transparent,
            var(--shiny-cta-highlight),
            transparent
          );
          mask-image: radial-gradient(circle at bottom, transparent 40%, black);
          opacity: 0.6;
          
          animation: var(--animation) var(--duration),
                     var(--animation) calc(var(--duration) / 0.4) reverse paused;
          animation-composition: add;
        }

        .button-text {
          z-index: 1;
          position: relative;
        }

        .button-text::before {
          width: 370px;
          height: 90px;
          box-shadow: inset 0 -1ex 2rem 4px var(--shiny-cta-highlight);
          opacity: 0;
          transition: opacity 1500ms cubic-bezier(0.25, 1, 0.5, 1);
        }

        .shiny-button:hover,
        .shiny-button:focus-visible {
          --gradient-percent: 20%;
          --gradient-angle-offset: 95deg;
          --gradient-shine: var(--shiny-cta-highlight-subtle);
        }

        .shiny-button:hover .button-text::before {
          opacity: 1;
        }

        .shiny-button:hover,
        .shiny-button:hover::before,
        .shiny-button:hover::after {
          animation-play-state: running;
        }

        @keyframes gradient-angle {
          to {
            --gradient-angle: 360deg;
          }
        }

        @keyframes shimmer {
          to {
            transform: rotate(360deg);
          }
        }

        @media only screen and (max-width: 600px) {
          .shiny-button {
            padding: 1.25rem 1.5rem;
            font-size: 14px;
          }
          
          .button-text::before {
            width: 280px;
            height: 70px;
          }
        }
      `}</style>

      {/* --- NAVBAR SIMPLIFICADO --- */}
      <nav className="relative z-20 flex items-center justify-between px-8 py-6 max-w-7xl mx-auto">
        {/* Logo */}
        <div className="flex items-center gap-2 group cursor-pointer">
          <div className="relative">
            <div className="absolute -inset-1 bg-gradient-to-r from-cyan-400 to-fuchsia-600 rounded-full blur opacity-75 group-hover:opacity-100 transition duration-200"></div>
            <img src="/oasis-logo.ico" alt="Oasis" className="relative w-8 h-8 rounded-full" />
          </div>
          <span className="text-2xl font-bold tracking-widest italic font-mono">OASIS</span>
        </div>
      </nav>

      {/* --- MAIN CONTENT --- */}
      <main className="relative z-10 flex flex-col justify-center h-full px-8 pb-20 max-w-7xl mx-auto">
        
        {/* Headline */}
        <h1 className="font-sans font-black text-5xl md:text-8xl leading-none mb-6 max-w-5xl italic uppercase">
          <span className="text-transparent bg-clip-text bg-gradient-to-r from-white via-gray-200 to-gray-500">Entre no</span> <br />
          <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 via-blue-500 to-fuchsia-600 drop-shadow-[0_0_10px_rgba(0,0,0,0.5)]">
            OASIS.
          </span>
        </h1>

        {/* Subheadline */}
        <p className="text-lg md:text-xl text-gray-300 max-w-xl mb-10 font-medium leading-relaxed border-l-2 border-white/20 pl-6">
          Experimente imersão infinita com nossa comunidade. Filmes, séries, rankings e muito mais para os verdadeiros jogadores.
        </p>

        {/* CTA Button */}
        <div className="flex flex-wrap gap-6">
          <Link 
            to="/login"
            className="shiny-button"
          >
            <span className="button-text">Entrar Agora!</span>
          </Link>
        </div>

        {/* Floating Annotation */}
        <div className="absolute right-20 top-1/3 hidden lg:block">
          <div className="flex items-end gap-4">
            <div className="mb-2 text-right">
              <p className="text-xs text-cyan-400 font-mono bg-black/80 px-2 py-1 border border-cyan-500/30">
                &lt;system_status&gt;<br/>OASIS: ONLINE
              </p>
            </div>
            <div className="relative">
              <div className="absolute -inset-2 bg-cyan-500/20 rounded-full blur-md animate-pulse"></div>
              <div className="w-3 h-3 bg-cyan-400 rounded-full"></div>
              <div className="w-px h-24 bg-gradient-to-b from-cyan-400 to-transparent absolute top-3 left-1.5"></div>
            </div>
          </div>
        </div>

      </main>

      {/* --- BOTTOM BAR / HUD --- */}
      <div className="absolute bottom-0 left-0 right-0 z-20 border-t border-white/10 bg-black/80 backdrop-blur-xl">
        <div className="max-w-7xl mx-auto px-8 py-5 flex flex-col md:flex-row items-center justify-between gap-6">
          
          {/* Left: Release Date */}
          <div className="flex items-center gap-2">
             <div className="w-2 h-2 bg-green-500 rounded-full animate-ping"></div>
             <span className="text-xs font-mono text-green-400 uppercase tracking-widest">Server Online: SP-BRAZIL</span>
          </div>

          {/* Center: Stats (HUD Style) */}
          <div className="flex items-center gap-8 md:gap-16 text-sm font-bold font-mono">
            <div className="flex items-center gap-3 group">
              <Users size={20} className="text-fuchsia-500 group-hover:animate-pulse" />
              <div className="flex flex-col">
                  <span className="text-[10px] text-gray-500 uppercase">Usuarios Ativos</span>
                  <span className="text-gray-200">43</span>
              </div>
            </div>
            
            <div className="w-px h-8 bg-gradient-to-b from-transparent via-white/20 to-transparent hidden md:block"></div>
            
            <div className="flex items-center gap-3 group">
              <Zap size={20} className="text-yellow-400 group-hover:animate-pulse" />
              <div className="flex flex-col">
                  <span className="text-[10px] text-gray-500 uppercase">Latency</span>
                  <span className="text-gray-200">12ms</span>
              </div>
            </div>

            <div className="w-px h-8 bg-gradient-to-b from-transparent via-white/20 to-transparent hidden md:block"></div>

            <div className="flex items-center gap-3">
               <div className="flex flex-col items-end leading-none">
                  <span className="text-xl font-black text-cyan-400 drop-shadow-[0_0_5px_rgba(34,211,238,0.8)]">240</span>
                  <span className="text-[10px] text-gray-400 tracking-widest">FPS LOCKED</span>
               </div>
            </div>
          </div>
        </div>
      </div>

    </div>
  );
};

export default Landing;
